import React, { useEffect, useRef, useState } from "react"
import { plural } from "./plannerLabels"
import { UNCONFIRMED } from "./proposalMessages"
import { languageLabel, sendingMessage } from "./sendingLabels"
import PlannerDialog from "./PlannerDialog"

// "Review and send" (ticket 16): whom each list goes to, how, in which language and what it carries. It is the confirmation, as for
// publishing: there is no Undo after it. Each organizer's list is sent as a command of its own, one after the other; each row then says
// how its sending ended. `onClose(results)` gets `[{ row, result }]` (none when cancelled).
const countsOf = row => [row.drafts + row.notSentYet && `${row.drafts + row.notSentYet} new`, row.changed && `${row.changed} changed`,
  row.withdrawn && `${row.withdrawn} withdrawn`].filter(Boolean).join(" · ") || "the list again"
const RESULT = { saved: row => (row.how === "email" ? "Sent: the email is on its way" : "Marked as sent by you"), error: () => "Not sent", unknown: () => "Not confirmed yet" }

export default function SendReviewDialog({ rows, answerBy, commands, onClose }) {
  const [phase, setPhase] = useState("confirm")
  const [results, setResults] = useState({})
  const collected = useRef([])
  // Closed with the page while it sends: the sendings already confirmed go on, unobserved.
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])
  const emails = rows.filter(row => row.how === "email").length
  const drafts = rows.reduce((sum, row) => sum + row.drafts, 0)
  const send = async () => {
    setPhase("sending")
    for (const row of rows) {
      if (alive.current) setResults(current => ({ ...current, [row.organizerId]: { status: "sending" } }))
      // eslint-disable-next-line no-await-in-loop
      const result = await commands.sendList(row.body)
      collected.current.push({ row, result: result || { status: "unknown" } })
      if (alive.current) setResults(current => ({ ...current, [row.organizerId]: result || { status: "unknown" } }))
    }
    if (alive.current) setPhase("done")
  }
  const label = emails && emails < rows.length ? `Send ${plural(emails, "email")} and mark ${rows.length - emails} as sent by me`
    : emails ? `Send ${plural(emails, "email")}` : `Mark ${rows.length} as sent by me`
  const close = () => {
    if (phase === "sending") return
    onClose(phase === "done" ? collected.current : [])
  }
  return <PlannerDialog wide title={phase === "confirm" ? `Send to ${plural(rows.length, "organizer")}?` : phase === "done" ? "Sending finished" : "Sending…"}
    sub={phase === "confirm" ? `Check whom each list goes to. ${plural(drafts, "new proposal")} ${drafts === 1 ? "becomes" : "become"} Proposed as soon as you send.`
      : "Each email is followed until the mail server accepts it: Waiting for answer shows its delivery."}
    closeLabel={phase === "done" ? "Done" : "Cancel"} onClose={close}
    actions={phase === "confirm" && <button type="button" className="btn btn-primary" onClick={send}>{label}</button>}>
    <div role="table" aria-label="Lists to send">
      <div role="row" className="ep-send-row ep-list-head">
        {["Organizer", "Goes to", "Proposals", phase === "confirm" ? "Language" : "Result"].map(column => <span role="columnheader" key={column}>{column}</span>)}
      </div>
      {rows.map(row => {
        const result = results[row.organizerId]
        return <div role="row" key={row.organizerId} className="ep-send-row">
          <span role="cell" className="ep-stack"><b>{row.name}</b></span>
          <span role="cell" className="ep-stack"><span>{row.how === "email" ? row.recipients.join(", ") : "You send it yourself"}</span>
            <span className="ep-meta">{row.how === "email" ? "Email from Sportya" : "XLSX or text, outside the platform"}</span></span>
          <span role="cell" className="ep-stack"><span>{countsOf(row)}</span><span className="ep-meta">{`${row.listSize} in the list`}</span></span>
          <span role="cell" className="ep-stack">{!result ? languageLabel(row.language) : result.status === "sending" ? "Sending…" : <>
            <span className={`ep-mark ${result.status === "saved" ? "ep-mark-ok" : "ep-mark-error"}`}>{(RESULT[result.status] || RESULT.error)(row)}</span>
            {result.status === "error" && <span className="ep-meta">{sendingMessage(result.code, result.details)}</span>}
            {result.status === "unknown" && <span className="ep-meta">{result.message || UNCONFIRMED}</span>}
          </>}</span>
        </div>
      })}
    </div>
    {phase === "confirm" && <p className="ep-meta mb-0">{`A list that asks for answers asks for them by ${answerBy} (14 days); passing that day changes nothing by itself.`}</p>}
    {phase === "confirm" && emails > 0 && <p className="ep-banner ep-banner-warn mb-0">An email cannot be recalled. If something is wrong later, change the proposal and
      send the updated list.</p>}
  </PlannerDialog>
}
